/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace hypergeometric
*/
var hypergeometric = {};

/**
* @name cdf
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/cdf}
*/
import cdf from './../../../../base/dists/hypergeometric/cdf';
setReadOnly( hypergeometric, 'cdf', cdf );

/**
* @name Hypergeometric
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/ctor}
*/
import Hypergeometric from './../../../../base/dists/hypergeometric/ctor';
setReadOnly( hypergeometric, 'Hypergeometric', Hypergeometric );

/**
* @name kurtosis
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/kurtosis}
*/
import kurtosis from './../../../../base/dists/hypergeometric/kurtosis';
setReadOnly( hypergeometric, 'kurtosis', kurtosis );

/**
* @name logpmf
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/logpmf}
*/
import logpmf from './../../../../base/dists/hypergeometric/logpmf';
setReadOnly( hypergeometric, 'logpmf', logpmf );

/**
* @name mean
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/mean}
*/
import mean from './../../../../base/dists/hypergeometric/mean';
setReadOnly( hypergeometric, 'mean', mean );

/**
* @name mode
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/mode}
*/
import mode from './../../../../base/dists/hypergeometric/mode';
setReadOnly( hypergeometric, 'mode', mode );

/**
* @name pmf
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/pmf}
*/
import pmf from './../../../../base/dists/hypergeometric/pmf';
setReadOnly( hypergeometric, 'pmf', pmf );

/**
* @name quantile
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/quantile}
*/
import quantile from './../../../../base/dists/hypergeometric/quantile';
setReadOnly( hypergeometric, 'quantile', quantile );

/**
* @name skewness
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/skewness}
*/
import skewness from './../../../../base/dists/hypergeometric/skewness';
setReadOnly( hypergeometric, 'skewness', skewness );

/**
* @name stdev
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/stdev}
*/
import stdev from './../../../../base/dists/hypergeometric/stdev';
setReadOnly( hypergeometric, 'stdev', stdev );

/**
* @name variance
* @memberof hypergeometric
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric/variance}
*/
import variance from './../../../../base/dists/hypergeometric/variance';
setReadOnly( hypergeometric, 'variance', variance );


// EXPORTS //

export default hypergeometric;
